import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import SectionHeader from '../../../util/component/SectionHeader';
import {Box, Button, Grid, Paper, useTheme} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {GlobalContext} from '../../../util/state/GlobalContextProvider';
import * as yup from 'yup';
import {FormProvider, useForm} from 'react-hook-form';
import OperatorData from '../component/OperatorData';
import CustomContentWrapper from '../../../util/component/CustomContentWrapper';
import {yupResolver} from "@hookform/resolvers/yup";
import axios from "axios";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {useMutation, useQuery} from "@tanstack/react-query";
import CustomLoader from "../../../util/component/CustomLoader";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {operatorDetailStructure} from "../state/operatorState";
import {useParams} from "react-router-dom";
import {useHasScope} from "../../../config/globalConfig";
import NotAuthorizedDialog from "../../authentication/component/NotAuthorizedDialog";

interface OperatorUpdateProps {
    id: number;
    username: string;
    password: string;
    name: string;
    surname: string;
    email: string;
    operatorType: number;
    legalEntities: any[];
    dealers: any[];
    mobilityStores: any[];
    logisticCompounds: any[];
    departments: any[];
    profiles: any[];
}

const OperatorUpdate = () => {
    const hasScope = useHasScope;
    const {id} = useParams()
    const [currentOperator, setCurrentOperator] = useState<any>(null);
    const theme = useTheme();
    const {t} = useTranslation(['operator']);
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [legalEntities] = useState<any[]>(globalData.user?.legalEntities);
    const [dealers] = useState<any[]>(globalData.user?.dealers);
    const [mobilityStores] = useState<any[]>(globalData.user?.mobilityStores);
    const [logisticCompounds] = useState<any[]>(globalData.user?.logisticCompounds);
    const [departments] = useState<any[]>(globalData.user?.departments);
    const [profiles, setProfiles] = useState<any[]>([]);
    const [operatorTypes, setOperatorTypes] = useState<any[]>([]);
    const setOperatorDetailStructure = useSetRecoilState(operatorDetailStructure);
    const {profileDtos} = useRecoilValue(operatorDetailStructure);

    useEffect(() => {
        setProfiles(profileDtos);
    }, [profileDtos]);

    useEffect(() => {
        setOperatorTypes([
            {code: 0, name: t('label.operatorType_0')},
            {code: 1, name: t('label.operatorType_1')},
            {code: 2, name: t('label.operatorType_2')}
        ])
    }, [t]);

    useEffect(() => {
        axios.post('/operator/find/' + id).then((response) => {
            let user = response.data;
            if (user.mobilityStores && user.mobilityStores[0]?.name === 'all') {
                user.mobilityStores[0].name = t('label.all');
                user.mobilityStores = [user.mobilityStores[0]];
            }
            if (user.logisticCompounds && user.logisticCompounds[0]?.name === 'all') {
                user.logisticCompounds[0].name = t('label.all');
                user.logisticCompounds = [user.logisticCompounds[0]];
            }
            setCurrentOperator(user);
        }).catch((error) => {
            console.log(error);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.tryLater'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });

    }, [id]);

    const validationSchema = yup.object().shape({
        username: yup
            .string()
            .trim()
            .lowercase()
            .required('message.validation.fieldRequired'),
        password: yup
            .string()
            .trim()
            .required('message.validation.fieldRequired'),
        name: yup
            .string()
            .trim()
            .lowercase()
            .required('message.validation.fieldRequired'),
        surname: yup
            .string()
            .trim()
            .lowercase()
            .required('message.validation.fieldRequired'),
        email: yup
            .string()
            .trim()
            .lowercase()
            .required('message.validation.fieldRequired')
            .email('message.validation.emailNotValid'),
        operatorType: yup
            .number()
            .required('message.validation.fieldRequired'),
        legalEntities: yup
            .array()
            .min(1, 'message.validation.fieldRequired'),
        dealers: yup
            .array()
            .min(1, 'message.validation.fieldRequired'),
        departments: yup
            .array()
            .min(1, 'message.validation.fieldRequired'),
        profiles: yup
            .array().min(1, 'message.validation.fieldRequired'),
    });

    const methods = useForm<OperatorUpdateProps>({
        resolver: yupResolver(validationSchema),
        defaultValues: useMemo(() => ({
            id: currentOperator?.id,
            username: currentOperator?.username,
            password: currentOperator?.password,
            name: currentOperator?.name,
            surname: currentOperator?.surname,
            email: currentOperator?.email,
            operatorType: currentOperator?.operatorType,
            legalEntities: currentOperator?.legalEntities,
            dealers: currentOperator?.dealers,
            mobilityStores: currentOperator?.mobilityStores,
            logisticCompounds: currentOperator?.logisticCompounds,
            departments: currentOperator?.departments,
            profiles: currentOperator?.profiles,
        }), [currentOperator])
    });

    const {reset, handleSubmit} = methods;

    useEffect(() => {
        reset(currentOperator)
    }, [currentOperator]);

    const updOnSuccess = useCallback((response: any) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('translation:success.updateOperation'),
                    type: snackbarTypes.success,
                    show: true,
                    autoClose: true,
                },
            },
        });
    }, [dispatchGlobalData, t]);

    const updOnError = useCallback((error: any) => {
        console.log(error);
        if (error.response.status === 400) {
            let errorMsg = t('translation:error.badRequest');
            JSON.parse(error.response.data.errorDetails).forEach((e: string) => {
                errorMsg = errorMsg.concat('\n').concat(t('message.error.'.concat(e)));
            });
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: errorMsg,
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('translation:error.updateOperation'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        }
    }, [dispatchGlobalData, t]);

    const updateOperator = useMutation({
        mutationFn: (params: any) => {
            return axios({
                method: 'put',
                url: '/operator',
                data: params,
            })
        },
        onSuccess: updOnSuccess,
        onError: updOnError
    })

    const findOperatorDetailStructure = () => {
        return axios.get('/operator/structure')
    }

    const onSuccess = (response: any) => {
        setOperatorDetailStructure(response.data);
    }
    const onError = (error: any) => {
        console.log(error);
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('message.error.tryLater'),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true
                }
            }
        });
    }

    const {isFetching} = useQuery(['findOperatorDetailStructure'], findOperatorDetailStructure, {
        onSuccess,
        onError
    })

    return (

        <Box>
            <SectionHeader
                title={t('label.updateOperator')}
            />
            <CustomContentWrapper>
                {hasScope('SCOPE_VIEW_OPERATOR') ? <FormProvider {...methods}>
                    <form onSubmit={handleSubmit((params: any) => updateOperator.mutate(params))}>
                        <Paper style={{
                            borderRadius: "4px",
                            padding: theme.spacing(1) + ' ' + theme.spacing(2),
                            marginBottom: theme.spacing(2)
                        }}
                               elevation={1}
                        >
                            <Grid container spacing={2}>
                                <OperatorData
                                    legalEntities={legalEntities}
                                    dealers={dealers}
                                    mobilityStores={mobilityStores}
                                    logisticCompounds={logisticCompounds}
                                    departments={departments}
                                    profiles={profiles}
                                    operatorTypes={operatorTypes}
                                    state={'edit'}
                                />
                                {hasScope('SCOPE_EDIT_OPERATOR') && <Grid item xs={12}>
                                    <Box mt={2}>
                                        <Button
                                            type="submit"
                                            fullWidth
                                        >
                                            {t('action.save')}
                                        </Button>
                                    </Box>
                                </Grid>}
                            </Grid>
                        </Paper>
                    </form>
                </FormProvider>
                : <NotAuthorizedDialog/>}
            </CustomContentWrapper>
            <CustomLoader loading={isFetching || updateOperator.isLoading}/>
        </Box>

    );
};

export default OperatorUpdate;

